import React, { useCallback, useEffect, useState } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { getApiUrl } from '../config';
import AddMatchModal from './AddMatchModal';
import GoalModal from './GoalModal';

interface DayPerf {
  label: string;
  total: number;
  unbeatenPct: number;
}

interface PerformanceStats {
  hasData: boolean;
  total: number;
  wins: number;
  draws: number;
  losses: number;
  unbeaten: number;
  unbeatenPct: number;
  avgGoalDiff: number | null;
  avgWinMargin: number | null;
  currentStreak: number;
  bestStreak: number;
  startDate: string;
  mostActiveDay: DayPerf | null;
  bestDay: DayPerf | null;
  playDays: string[];
  performanceByDay: DayPerf[];
  objective: {
    targetPct: number;
    currentPct: number;
    reached: boolean;
    matchesToTarget: number;
    deadline: string | null;
    daysRemaining: number | null;
  };
  cumulativeTrend: Array<{ date: string; result: string; diff: number | null; unbeatenPct: number }>;
}

interface Props {
  userId: string;
  isMobile?: boolean;
}

const GREEN = '#00E676';
const RED = '#FF5252';
const YELLOW = '#FFD600';
const BLUE = '#00B8D9';
const CARD_BG = '#23272A';
const CARD_BORDER = '#33383B';
const MUTED = '#B0BEC5';

function fmtSigned(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return n > 0 ? `+${n}` : `${n}`;
}

function fmtDDMM(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

const Stat: React.FC<{ label: string; value: string; sub?: string; color: string; isMobile?: boolean }> = ({
  label,
  value,
  sub,
  color,
  isMobile,
}) => (
  <div
    style={{
      flex: 1,
      minWidth: isMobile ? '120px' : '150px',
      background: CARD_BG,
      border: `1px solid ${color}44`,
      borderRadius: '14px',
      padding: isMobile ? '12px' : '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    }}
  >
    <span style={{ fontSize: '11px', fontWeight: 600, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </span>
    <span style={{ fontSize: isMobile ? '24px' : '28px', fontWeight: 900, color }}>{value}</span>
    {sub && <span style={{ fontSize: '12px', color: MUTED }}>{sub}</span>}
  </div>
);

// Tooltip oscuro custom para el gráfico de tendencia.
const TrendTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const value = payload[0].value as number;
  return (
    <div
      style={{
        background: '#181A1B',
        border: `1px solid ${CARD_BORDER}`,
        borderRadius: '10px',
        padding: '8px 12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{ color: MUTED, fontSize: '12px', marginBottom: '2px' }}>{label}</div>
      <div style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: 700 }}>{value}% sin perder</div>
    </div>
  );
};

// Tendencia acumulada con recharts (% sin perder + línea de objetivo).
const TrendChart: React.FC<{
  data: Array<{ date: string; unbeatenPct: number }>;
  target: number;
}> = ({ data, target }) => {
  if (data.length < 2) return null;
  // Mostrar ~5-6 labels en el eje X sin saturar.
  const interval = Math.max(0, Math.ceil(data.length / 6) - 1);
  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart data={data} margin={{ top: 12, right: 16, bottom: 4, left: -8 }}>
        <CartesianGrid stroke={CARD_BORDER} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="date"
          interval={interval}
          tick={{ fill: MUTED, fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: CARD_BORDER }}
        />
        <YAxis
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fill: MUTED, fontSize: 11 }}
          tickLine={false}
          axisLine={{ stroke: CARD_BORDER }}
          width={44}
        />
        <Tooltip content={<TrendTooltip />} cursor={{ stroke: CARD_BORDER, strokeWidth: 1 }} />
        <ReferenceLine
          y={target}
          stroke={BLUE}
          strokeDasharray="5 5"
          label={{ value: 'Objetivo', position: 'insideTopRight', fill: BLUE, fontSize: 11 }}
        />
        <Line
          type="monotone"
          dataKey="unbeatenPct"
          stroke={GREEN}
          strokeWidth={2.5}
          dot={false}
          activeDot={{ r: 4, fill: GREEN, stroke: '#181A1B', strokeWidth: 2 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const PerformanceInsights: React.FC<Props> = ({ userId, isMobile = false }) => {
  const [stats, setStats] = useState<PerformanceStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const res = await fetch(getApiUrl(`/api/users/${userId}/performance-stats`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStats(await res.json());
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const titleStyle: React.CSSProperties = {
    fontSize: isMobile ? '15px' : '18px',
    fontWeight: 700,
    color: '#FFFFFF',
    margin: '0 0 12px 0',
  };

  const addButton = (
    <button
      onClick={() => setShowAddModal(true)}
      style={{
        padding: '8px 14px',
        borderRadius: '10px',
        border: `1.5px solid ${GREEN}`,
        background: 'transparent',
        color: GREEN,
        fontWeight: 700,
        fontSize: '13px',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      + Agregar partido
    </button>
  );

  const modal = (
    <AddMatchModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} onSaved={fetchStats} />
  );

  if (loading) return <div style={{ color: MUTED, padding: '20px' }}>Cargando rendimiento…</div>;

  // Empty state: onboarding "una vez" para users sin partidos cargados.
  if (!stats || !stats.hasData || stats.total === 0) {
    return (
      <div style={{ marginBottom: '20px' }}>
        <div
          style={{
            background: CARD_BG,
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: '16px',
            padding: isMobile ? '20px' : '28px',
            textAlign: 'center',
          }}
        >
          <h3 style={{ ...titleStyle, margin: '0 0 8px 0' }}>📊 Empezá tu registro</h3>
          <p style={{ color: MUTED, fontSize: '14px', margin: '0 0 16px 0' }}>
            ¿Ya venías llevando tus partidos? Cargalos de una.
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '12px 20px',
              borderRadius: '12px',
              border: 'none',
              background: GREEN,
              color: '#181A1B',
              fontWeight: 800,
              fontSize: '15px',
              cursor: 'pointer',
            }}
          >
            + Agregar partido
          </button>
        </div>
        {modal}
      </div>
    );
  }

  const winPct = Math.round((stats.wins / stats.total) * 100);
  const drawPct = Math.round((stats.draws / stats.total) * 100);
  const lossPct = Math.round((stats.losses / stats.total) * 100);

  const streakColor = stats.currentStreak >= 0 ? GREEN : RED;
  const cardStyle: React.CSSProperties = {
    background: CARD_BG,
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: '16px',
    padding: isMobile ? '14px' : '18px',
    marginBottom: '16px',
  };
  const row: React.CSSProperties = { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' };

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <h3 style={{ ...titleStyle, margin: 0 }}>📊 Rendimiento</h3>
        {addButton}
      </div>

      <div style={row}>
        <Stat label="Ganados" value={`${stats.wins}`} sub={`${winPct}%`} color={GREEN} isMobile={isMobile} />
        <Stat label="Empatados" value={`${stats.draws}`} sub={`${drawPct}%`} color={YELLOW} isMobile={isMobile} />
        <Stat label="Perdidos" value={`${stats.losses}`} sub={`${lossPct}%`} color={RED} isMobile={isMobile} />
      </div>

      <div style={row}>
        <Stat label="Partidos" value={`${stats.total}`} color={GREEN} isMobile={isMobile} />
        <Stat label="Sin perder" value={`${stats.unbeatenPct}%`} sub={`${stats.unbeaten} de ${stats.total}`} color={GREEN} isMobile={isMobile} />
        <Stat label="Racha actual" value={fmtSigned(stats.currentStreak)} sub="sin perder" color={streakColor} isMobile={isMobile} />
        <Stat label="Mejor racha" value={`${stats.bestStreak}`} sub="sin perder" color={BLUE} isMobile={isMobile} />
      </div>

      <div style={row}>
        <Stat label="Prom. goles" value={fmtSigned(stats.avgGoalDiff)} sub="diferencia/partido" color={GREEN} isMobile={isMobile} />
        <Stat label="Margen vict." value={fmtSigned(stats.avgWinMargin)} sub="goles cuando ganás" color={GREEN} isMobile={isMobile} />
        <Stat label="Día más activo" value={stats.mostActiveDay?.label ?? '—'} sub={stats.mostActiveDay ? `${stats.mostActiveDay.total} partidos` : ''} color={YELLOW} isMobile={isMobile} />
        <Stat label="Mejor día" value={stats.bestDay?.label ?? '—'} sub={stats.bestDay ? `${stats.bestDay.unbeatenPct}% sin perder` : ''} color={GREEN} isMobile={isMobile} />
        <Stat label="Inicio" value={fmtDDMM(stats.startDate)} sub="primer partido" color={BLUE} isMobile={isMobile} />
        <Stat label="Días de juego" value={stats.playDays.join(' · ') || '—'} sub="habituales" color={GREEN} isMobile={isMobile} />
      </div>

      {/* Objetivo */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <span style={{ ...titleStyle, margin: 0 }}>🎯 Objetivo</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '18px', fontWeight: 900, color: YELLOW }}>
              {stats.objective.currentPct}% / {stats.objective.targetPct}%
            </span>
            <button
              onClick={() => setShowGoalModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: BLUE,
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              cambiar
            </button>
          </div>
        </div>
        <div style={{ height: '10px', background: '#181A1B', borderRadius: '6px', overflow: 'hidden', marginBottom: '8px' }}>
          <div
            style={{
              width: `${Math.min(100, (stats.objective.currentPct / stats.objective.targetPct) * 100)}%`,
              height: '100%',
              background: YELLOW,
            }}
          />
        </div>
        <span style={{ fontSize: '13px', color: '#FFFFFF' }}>
          {stats.objective.reached
            ? `¡Alcanzaste tu objetivo de ${stats.objective.targetPct}% sin perder!`
            : `Necesitás ganar o empatar ${stats.objective.matchesToTarget} partido${
                stats.objective.matchesToTarget === 1 ? '' : 's'
              } seguido${stats.objective.matchesToTarget === 1 ? '' : 's'} para alcanzar el objetivo.`}
        </span>
        {stats.objective.deadline && stats.objective.daysRemaining !== null && (
          <div
            style={{
              fontSize: '13px',
              marginTop: '6px',
              color: stats.objective.daysRemaining < 0 ? RED : MUTED,
            }}
          >
            {stats.objective.daysRemaining < 0
              ? `📅 se pasó la fecha límite (${fmtDDMM(stats.objective.deadline)})`
              : stats.objective.daysRemaining === 0
              ? `📅 hasta ${fmtDDMM(stats.objective.deadline)} · ¡último día!`
              : `📅 hasta ${fmtDDMM(stats.objective.deadline)} · faltan ${stats.objective.daysRemaining} día${
                  stats.objective.daysRemaining === 1 ? '' : 's'
                }`}
          </div>
        )}
      </div>

      {/* Tendencia acumulada */}
      {stats.cumulativeTrend.length > 1 && (
        <div style={cardStyle}>
          <span style={titleStyle}>📈 Tendencia acumulada</span>
          <TrendChart
            data={stats.cumulativeTrend.map((p) => ({ date: p.date, unbeatenPct: p.unbeatenPct }))}
            target={stats.objective.targetPct}
          />
        </div>
      )}

      {/* Rendimiento por día */}
      {stats.performanceByDay.length > 0 && (
        <div style={cardStyle}>
          <span style={titleStyle}>📅 Rendimiento por día</span>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', height: '140px', paddingTop: '8px' }}>
            {stats.performanceByDay.map((d) => (
              <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', justifyContent: 'flex-end' }}>
                <span style={{ fontSize: '12px', color: '#FFFFFF', marginBottom: '4px' }}>{d.unbeatenPct}%</span>
                <div
                  style={{
                    width: '70%',
                    height: `${Math.max(2, d.unbeatenPct)}%`,
                    background: d.unbeatenPct >= stats.objective.targetPct ? GREEN : d.unbeatenPct >= 50 ? YELLOW : RED,
                    borderRadius: '6px 6px 0 0',
                  }}
                />
                <span style={{ fontSize: '11px', color: MUTED, marginTop: '6px' }}>{d.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {modal}
      <GoalModal
        isOpen={showGoalModal}
        onClose={() => setShowGoalModal(false)}
        onSaved={fetchStats}
        initialTargetPct={stats.objective.targetPct}
        initialDeadline={stats.objective.deadline}
      />
    </div>
  );
};

export default PerformanceInsights;
